import React, { useMemo } from 'react'
import useApiService from 'services/api'
import LoadingScreen from 'components/Layout/LoadingScreen'
import {
  CompanyPageLayout,
  CompanyContentSections,
} from 'components/NewCompanyPage'
import { DoorsModal } from 'components/NewCompanyPage/Modals/DoorsModal/DoorsModal'
import useDocumentTitle from 'components/useDocumentTitle'
import { ConvenienceDoorTable } from 'components/DataExpansionComponents/GooglePlaces/ConvenienceDoors/ConvenienceDoorsTable/ConvenienceDoorsTable'
import {
  buildGooglePlaceSummaryContent,
  buildGooglePlaceLocationContent,
  GOOGLE_PLACE_CONFIGS,
} from './utils/googlePlacesHelpers'
import { CONVENIENCE_DOOR_TABLE_KEY } from 'constants/tableQueryKeys'
import { useHideFooter } from 'context/layout'

export function NewConveniencePage({
  convenienceID,
}: {
  convenienceID?: number
}) {
  const api = useApiService()
  const { data, isLoading } = api.useGetConvenience(convenienceID)

  // Hide footer on company pages (must be called before loading check)
  useHideFooter()

  // Set document title
  useDocumentTitle(
    `${data?.cstore?.name || 'Not Found'} (Convenience Store) - First Bite`
  )

  const [showDoorsModal, setShowDoorsModal] = React.useState(false)

  const entityBreadcrumbs = useMemo(
    () =>
      data?.cstore?.name ? [{ title: 'Company', label: data.cstore.name }] : [],
    [data?.cstore?.name]
  )

  if (isLoading) {
    return <LoadingScreen />
  }

  const config = GOOGLE_PLACE_CONFIGS.convenience

  // Use helper functions to build content
  const summaryTabContent = buildGooglePlaceSummaryContent({
    onViewDoorsClick: () => setShowDoorsModal(true),
    placeData: data?.cstore,
    hideDoorsMetric: false,
  })

  const locationDetailsTabContent = buildGooglePlaceLocationContent(
    data?.cstore
  )

  return (
    <>
      <CompanyPageLayout
        chainProxyId={data!.id!}
        breadcrumbRoot={config.breadcrumbRoot}
        breadcrumbs={[
          {
            title: config.breadcrumbTitle,
            breadcrumb: data!.cstore!.name!,
            url: undefined,
          },
        ]}
        entityBreadcrumbs={entityBreadcrumbs}
        categoryInfo={{
          categorySlug: 'retail',
          subCategorySlug: 'convenience',
        }}
        sidebar={{
          header: {
            name: data!.cstore!.name!,
            domain: data!.cstore!.domain!,
            logoUrl: undefined,
          },
          tabs: [
            {
              key: 'summary',
              title: 'Summary',
              content: summaryTabContent,
            },
            {
              key: 'location-details',
              title: 'Location Details',
              content: locationDetailsTabContent,
            },
          ],
        }}
      >
        <CompanyContentSections
          chainProxyId={data!.id!}
          entityID={convenienceID}
          companyName={data!.cstore!.name!}
          websiteAddress={data!.cstore!.domain!}
          companyTypeSlug="convenience"
          tableTypeSlug="convenience"
        />
      </CompanyPageLayout>

      {/* Doors Modal with Tabs */}
      {data && (
        <DoorsModal
          show={showDoorsModal}
          onHide={() => setShowDoorsModal(false)}
          forChainProxyId={data.id}
          chainData={{
            id: data.id,
            name: data.cstore?.name || '',
            domain: data.cstore?.domain,
            count: data.cstore?.doors_cnt,
          }}
          chainName={data.cstore?.name}
          subCategory={{
            name: config.modalName,
            slug: config.modalSlug,
            description: config.modalName,
          }}
          matchedTableComponent={ConvenienceDoorTable}
          matchedTableKey={`${CONVENIENCE_DOOR_TABLE_KEY}-${data.id}`}
          matchedTableProps={{
            hideCreateCompanyButton: true,
            baseFilters: { chain_proxy_id: data.id },
          }}
          unmatchedTableKeyPrefix="unmatched-convenience-doors-table"
        />
      )}
    </>
  )
}
